// Deposit Service - Handles deposit logic and API calls
// TODO: Replace simulated methods with actual API calls when backend is ready

export interface DepositData {
    amount: number;
    providerId: string | number; // Support both string (UUID from API) and number (legacy)
    providerName: string;
    userId?: string; // Optional for bypass mode
}

export interface DepositResponse {
    success: boolean;
    message: string;
    data?: {
        transactionId: string;
        amount: number;
        status: 'pending' | 'completed' | 'failed';
        timestamp: string;
    };
    error?: string;
}

export interface PaymentMethod {
    id: string;
    name: string;
    type: 'credit_card' | 'debit_card' | 'bank_transfer' | 'digital_wallet';
    lastFour?: string;
    expiryMonth?: number;
    expiryYear?: number;
}

class DepositService {
    private readonly MIN_AMOUNT = 10;
    private readonly MAX_AMOUNT = 1000;

    /**
     * Validates deposit amount
     */
    validateAmount(amount: number): { isValid: boolean; message: string } {
        if (amount < this.MIN_AMOUNT) {
            return {
                isValid: false,
                message: `Minimum deposit amount is $${this.MIN_AMOUNT}`
            };
        }

        if (amount > this.MAX_AMOUNT) {
            return {
                isValid: false,
                message: `The maximum deposit amount is $${this.MAX_AMOUNT}. Please enter a smaller amount.`
            };
        }

        return {
            isValid: true,
            message: 'Amount is valid'
        };
    }

    /**
     * Processes deposit request
     * TODO: Replace with actual API call
     */
    async processDeposit(depositData: DepositData): Promise<DepositResponse> {
        try {
            // Validate amount
            const validation = this.validateAmount(depositData.amount);
            if (!validation.isValid) {
                return {
                    success: false,
                    message: validation.message,
                    error: 'INVALID_AMOUNT'
                };
            }

            // TODO: Replace with actual API call
            // const response = await fetch('/api/deposit/process', {
            //     method: 'POST',
            //     headers: { 'Content-Type': 'application/json' },
            //     body: JSON.stringify(depositData)
            // });
            // const result = await response.json();

            // Simulate API call for now
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Simulate successful deposit
            const transactionId = `TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            return {
                success: true,
                message: `Deposit of $${depositData.amount.toFixed(2)} processed successfully`,
                data: {
                    transactionId,
                    amount: depositData.amount,
                    status: 'completed',
                    timestamp: new Date().toISOString()
                }
            };

        } catch (error) {
            console.error('Error processing deposit:', error);
            return {
                success: false,
                message: 'Failed to process deposit. Please try again.',
                error: 'PROCESSING_ERROR'
            };
        }
    }

    /**
     * Gets available payment methods for user
     * TODO: Replace with actual API call
     */
    async getPaymentMethods(): Promise<PaymentMethod[]> {
        try {
            // TODO: Replace with actual API call
            // const response = await fetch('/api/payment-methods');
            // const result = await response.json();

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 500));

            // Return mock payment methods
            return [
                {
                    id: 'pm_1',
                    name: 'Visa **** 4242',
                    type: 'credit_card',
                    lastFour: '4242',
                    expiryMonth: 12,
                    expiryYear: 2025
                },
                {
                    id: 'pm_2',
                    name: 'Mastercard **** 5555',
                    type: 'credit_card',
                    lastFour: '5555',
                    expiryMonth: 8,
                    expiryYear: 2026
                },
                {
                    id: 'pm_3',
                    name: 'PayPal Account',
                    type: 'digital_wallet'
                }
            ];

        } catch (error) {
            console.error('Error fetching payment methods:', error);
            return [];
        }
    }

    /**
     * BYPASS MODE - Skip authentication for demo purposes
     * TODO: Remove this method in production
     */
    async processDepositBypass(depositData: DepositData): Promise<DepositResponse> {
        console.log('🚀 BYPASS MODE: Processing deposit without authentication');
        console.log('Deposit Data:', depositData);

        // Simulate processing time
        await new Promise(resolve => setTimeout(resolve, 1000));

        const transactionId = `BYPASS_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        return {
            success: true,
            message: `BYPASS: Deposit of $${depositData.amount.toFixed(2)} processed successfully`,
            data: {
                transactionId,
                amount: depositData.amount,
                status: 'completed',
                timestamp: new Date().toISOString()
            }
        };
    }

    /**
     * Gets deposit limits for user
     */
    getDepositLimits(): { min: number; max: number; dailyLimit?: number } {
        return {
            min: this.MIN_AMOUNT,
            max: this.MAX_AMOUNT,
            dailyLimit: 5000 // Example daily limit
        };
    }
}

// Export singleton instance
export const depositService = new DepositService();
